import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getAirportUsers } from "@/queries/user";
import { Controller, UseFormReturn } from "react-hook-form";
import * as z from "zod";

export const teamSchema = z.object({
  name: z
    .string()
    .min(1, "Team name is required")
    .max(200, "Team name too long"),
  description: z.string().max(1000, "Description too long"),
  leader_id: z.number().optional(),
});

export type TeamFormValues = z.infer<typeof teamSchema>;

interface TeamFormProps {
  form: UseFormReturn<TeamFormValues>;
  onSubmit: (values: TeamFormValues) => void;
  isLoading: boolean;
  submitButtonText: string;
  onClose?: () => void;
}

export function TeamForm({
  form,
  onSubmit,
  isLoading,
  submitButtonText,
  onClose,
}: TeamFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const { data: airportUsers, isLoading: isLoadingUsers } = getAirportUsers();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Team Name</Label>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Enter team name"
              className="w-full"
            />
          )}
        />
        {errors.name && (
          <p className="text-red-500 text-sm">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder="Enter team description (optional)"
              className="w-full min-h-[80px]"
            />
          )}
        />
        {errors.description && (
          <p className="text-red-500 text-sm">{errors.description.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="leader">Leader</Label>
        <Controller
          name="leader_id"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={(value) =>
                field.onChange(value ? parseInt(value) : undefined)
              }
              value={field.value?.toString() || ""}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a team leader (optional)" />
              </SelectTrigger>
              <SelectContent>
                {isLoadingUsers ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    Loading users...
                  </div>
                ) : airportUsers && airportUsers.length > 0 ? (
                  airportUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No users available
                  </div>
                )}
              </SelectContent>
            </Select>
          )}
        />
        {errors.leader_id && (
          <p className="text-red-500 text-sm">{errors.leader_id.message}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Saving..." : submitButtonText}
      </Button>
    </form>
  );
}
